import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Fashion Nova–style “Added to bag” sheet (~75vh): opens on successful AJAX adds from product forms.
 */
class AddedToBagSheetComponent extends DialogComponent {
  /** @type {(e: Event) => void} */
  #onCart = this.#handleCartAdd.bind(this);

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.afterAdd !== 'sheet') return;
    document.addEventListener(CartAddEvent.eventName, this.#onCart);
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.#onCart);
    super.disconnectedCallback();
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartAdd(event) {
    const detail = event.detail;
    const data = detail?.data;
    if (data?.didError) return;
    const addedCount = Number(data?.itemCount ?? 1);
    if (!Number.isFinite(addedCount) || addedCount <= 0) return;

    const cart = /** @type {{ items?: Array<Record<string, unknown>>; item_count?: number; currency?: string } | undefined} */ (
      detail.resource
    );
    if (!cart?.items?.length) return;

    const variantId = String(detail.sourceId || '');
    let line = cart.items.find((/** @type {{ variant_id?: number | string }} */ i) => String(i.variant_id) === variantId);
    if (!line) line = cart.items[cart.items.length - 1];

    this.#renderLine(/** @type {Record<string, unknown>} */ (line), cart);
    this.#setViewBagCount(Number(cart.item_count) || cart.items.length);
    this.#primeRecommendations(Number(data.productId) || Number(line.product_id));

    document.dispatchEvent(
      new CustomEvent('mnj:bogo-grid-updated', { bubbles: true, composed: true, detail: { cart } })
    );

    this.showDialog();
  }

  /**
   * @param {Record<string, unknown>} line
   * @param {{ currency?: string }} cart
   */
  #renderLine(line, cart) {
    const root = this.querySelector('[data-mnj-added-line]');
    if (!root) return;

    const img = root.querySelector('[data-mnj-line-image]');
    const title = root.querySelector('[data-mnj-line-title]');
    const variant = root.querySelector('[data-mnj-line-variant]');
    const price = root.querySelector('[data-mnj-line-price]');

    const imageUrl = typeof line.image === 'string' ? line.image : '';
    if (img instanceof HTMLImageElement) {
      img.src = imageUrl || '';
      img.alt = String(line.product_title || '');
      img.hidden = !imageUrl;
    }

    if (title) title.textContent = String(line.product_title || '');
    if (variant) {
      const vt = String(line.variant_title || '').trim();
      variant.textContent = vt && vt !== 'Default Title' ? vt : '';
      variant.hidden = !variant.textContent;
    }

    const cents = Number(line.final_line_price);
    if (price) {
      const { moneyFormat, currency } = this.#moneyConfig();
      price.textContent = Number.isFinite(cents) ? formatMoney(cents, moneyFormat, currency || cart.currency || 'USD') : '';
    }
  }

  #moneyConfig() {
    const el = document.getElementById('mnj-added-bag-money-config');
    try {
      const j = el?.textContent ? JSON.parse(el.textContent) : {};
      return {
        moneyFormat: typeof j.moneyFormat === 'string' ? j.moneyFormat : '${{amount}}',
        currency: typeof j.currency === 'string' ? j.currency : 'USD',
      };
    } catch {
      return { moneyFormat: '${{amount}}', currency: 'USD' };
    }
  }

  /** @param {number} count */
  #setViewBagCount(count) {
    const btn = this.querySelector('[data-mnj-view-bag]');
    if (!(btn instanceof HTMLElement)) return;
    const template = btn.dataset.labelTemplate || 'View bag ({{ count }})';
    btn.textContent = template.replace(/\{\{\s*count\s*\}\}/g, String(count));
  }

  /** @param {number} productId */
  #primeRecommendations(productId) {
    const wrap = this.querySelector('[data-mnj-recs-wrap]');
    if (!(wrap instanceof HTMLElement)) return;
    if (wrap.dataset.showRecs !== 'true') {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;
    const pr = wrap.querySelector('product-recommendations');
    if (!(pr instanceof HTMLElement) || !productId) return;
    pr.dataset.recommendationsPerformed = 'false';
    pr.dataset.productId = String(productId);
  }

  /** @param {MouseEvent} e */
  handleViewBag = (e) => {
    e.preventDefault();
    const drawerFirst = this.dataset.cartIsDrawer === 'true';
    const cartUrl = this.dataset.cartUrl || '/cart';

    this.closeDialog().then(() => {
      if (drawerFirst) {
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
          /** @type {{ open: () => void }} */ (drawer).open();
          return;
        }
      }
      window.location.href = cartUrl;
    });
  };
}

if (!customElements.get('added-to-bag-sheet-component')) {
  customElements.define('added-to-bag-sheet-component', AddedToBagSheetComponent);
}
